import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { ActionKey, type ZipaJournal } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { formatAccountingInstant } from '../../../lib/accounting-time';
import { useAccountingDisplayTimezone } from '../../../hooks/useAccountingDisplayTimezone';

export function journalNextPage(page: number, count: number) {
  return count === 100 && page < 100000 ? page + 1 : null;
}
export function ZipaJournalPanel({ memberId }: { memberId: string }) {
  const actor = useAuthStore((s) => s.user?.id);
  const session = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const scope = JSON.stringify([actor, session, venue, memberId]);
  return <Journal key={scope} memberId={memberId} scope={scope} venue={venue} />;
}
function Journal({ memberId, scope, venue }: { memberId: string; scope: string; venue: string }) {
  const { t, i18n } = useTranslation('member');
  const displayTimezone = useAccountingDisplayTimezone();
  const actions = useAuthStore((s) => s.allowedActions);
  const system = useAuthStore((s) => s.user?.isSystemUser);
  const allowed =
    !!system ||
    [ActionKey.ACCOUNTING_READ, ActionKey.TOURNAMENT_UPDATE, ActionKey.TOURNAMENT_COMPLETE].some(
      (a) => actions?.includes(a),
    );
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['zipa-journal', memberId, scope, page],
    queryFn: ({ signal }) =>
      api.get<ZipaJournal>(`/members/${encodeURIComponent(memberId)}/zipa-journal`, {
        params: { page },
        signal,
      }),
    enabled: allowed && !!venue,
    retry: false,
  });
  const data = !query.isError && query.data?.page === page ? query.data : undefined;
  const value = (s: string | null) => s ?? t('zipaJournal.unavailable');
  if (!allowed) return <p role="status">{t('zipaJournal.forbidden')}</p>;
  return (
    <section
      className="space-y-3 rounded border border-base-300 p-4"
      aria-label={t('zipaJournal.title')}
    >
      <h3 className="font-semibold">{t('zipaJournal.title')}</h3>
      <p className="text-sm">{t('zipaJournal.paginationNotice')}</p>
      {query.isFetching && <p role="status">{t('zipaJournal.loading')}</p>}
      {query.isError && (
        <p role="alert">
          {t(
            isAxiosError(query.error) && query.error.response?.status === 403
              ? 'zipaJournal.forbidden'
              : 'zipaJournal.loadError',
          )}
        </p>
      )}
      {data?.rows.length === 0 && <p>{t('zipaJournal.empty')}</p>}
      <div className="space-y-3">
        {data?.rows.map((row) => (
          <article
            key={row.id}
            className="grid gap-2 rounded border border-base-300 p-3 text-sm break-all sm:grid-cols-2"
          >
            <p>
              {formatAccountingInstant(row.createdAt, displayTimezone ?? '', i18n.language) ??
                t('accounting:dailyLedgers.unavailable')}{' '}
              · {row.type}
            </p>
            <p className="font-mono tabular-nums">
              {value(row.before)} → {row.delta} → {value(row.after)} {data.unit}
            </p>
            <p>
              {t('zipaJournal.source')}: {row.sourceKind} · {value(row.sourceOrigin)}
            </p>
            <p>
              {t('zipaJournal.actor')}: {value(row.actorId)}
            </p>
            <p>
              {t('zipaJournal.tournament')}: {value(row.tournamentId)}
            </p>
            <p>
              {t('zipaJournal.operation')}: {value(row.operationKey)}
            </p>
            <p>{row.note}</p>
            <p>{row.id}</p>
          </article>
        ))}
      </div>
      <nav aria-label={t('zipaJournal.pagination')} className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="min-h-11 rounded border border-base-300 px-4 disabled:opacity-50"
          disabled={page === 1 || query.isFetching}
          onClick={() => setPage((p) => p - 1)}
        >
          {t('zipaJournal.previous')}
        </button>
        <span>{t('zipaJournal.page', { page })}</span>
        <button
          type="button"
          className="min-h-11 rounded border border-base-300 px-4 disabled:opacity-50"
          disabled={!data || journalNextPage(page, data.rows.length) === null || query.isFetching}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('zipaJournal.next')}
        </button>
        <button
          type="button"
          className="min-h-11 rounded border border-base-300 px-4"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          {t('zipaJournal.refresh')}
        </button>
      </nav>
    </section>
  );
}
